import React from 'react'
import { AdversaryNameLink } from './Filtering/AdversaryNameLink'
import { BlightCardLink } from './Filtering/BlightCardLink'
import { DifficultyLink } from './Filtering/DifficultyLink'
import { IslandStateLink } from './Filtering/IslandStateLink'
import { LayoutLink } from './Filtering/LayoutLink'
import { OutcomeLink } from './Filtering/OutcomeLink'
import { OutcomeTypeLink } from './Filtering/OutcomeTypeLink'
import { PlayerNameLink } from './Filtering/PlayerNameLink'
import { Play } from '../Common/SI'
import { SpiritLink } from './Filtering/SpiritLink'
import { TurnsLink } from './Filtering/TurnsLink'
import { AspectLink } from './Filtering/AspectLink'
import { DateFilterLink } from './Filtering/DateFilterLink'
import { LocationLink } from './Filtering/LocationLink'

export interface PlayCardProps {
	play: Play
	alwaysExpanded?: boolean
}

export function PlayCard(props: PlayCardProps): JSX.Element {
	const play = props.play

	const [expanded, setExpanded] = React.useState(!!props.alwaysExpanded)

	const duration = play.durationMins ? `(${Math.floor(play.durationMins / 60)}:${('0' + play.durationMins % 60).slice(-2)})` : null

	return (
		<div className={`play ${play.outcome.victory ? 'victory' : 'loss'} ${props.alwaysExpanded || expanded ? 'expanded' : null}`}>

			<div className='headline'>
				<div>
					<div className='date-time'><DateFilterLink dateTimeString={play.dateTimeString} /></div>
					<div className='duration'>{duration}</div>
					<div className='players'>
						{
							play.players.map((player, i) => (
								<React.Fragment key={i}>
									<PlayerNameLink player={player} />
									{i === play.players.length - 1 ? null : <React.Fragment>, </React.Fragment>}
								</React.Fragment>
							))
						}
					</div>
				</div>
				<div>
					<div className='play-number'>#{play.number}</div>
				</div>
			</div>

			<div className='summary'>
				<div className='parameters'>
					{
						play.adversaries.length === 0
							? <div className='adversaries'>No adversary</div>
							: <div className='adversaries'>
								{
									play.adversaries.map((a, i) =>
										<React.Fragment key={i}>
											<AdversaryNameLink adversaryName={a.name} /> {a.level}
											{i === play.adversaries.length - 1 ? null : <React.Fragment>, </React.Fragment>}
										</React.Fragment>
									)
								}
							</div>
					}
					{
						play.scenario && <div className='scenario'>{play.scenario}</div>
					}
					{
						play.additionalBoards > 0 && <div className='additional-boards'>+{play.additionalBoards} board{play.additionalBoards > 1 ? 's' : ''}</div>
					}
					{
						play.difficulty !== null && <div className='difficulty'><DifficultyLink difficulty={play.difficulty} /></div>
					}
					{
						play.layout && <div className='layout'>Layout: <LayoutLink layout={play.layout} /></div>
					}
					<div className='location'>Location: <LocationLink location={play.location} /></div>
				</div>

				<div className='result'>
					<div className='outcome'>
						<OutcomeLink victory={play.outcome.victory} />: <OutcomeTypeLink outcomeType={play.outcome?.type} />
					</div>

					{
						play.islandState &&
						<div className='island-state'>
							<span>
								<IslandStateLink islandState={play.islandState} />
								{
									play.islandState?.blightCards.length > 0 && <React.Fragment>: </React.Fragment>
								}
							</span>
							{
								play.islandState?.blightCards.length > 0 &&
								<span className='blight-cards'>
									{
										play.islandState.blightCards.map((bc, i) =>
											<span key={i}>
												<BlightCardLink blightCard={bc} />
											</span>
										)
									}
								</span>
							}
						</div>
					}

					{
						play.turns !== null && play.turns > 0 &&
						<div className='turns'>Turns: <TurnsLink turns={play.turns} /></div>
					}

					{
						play.score &&
						<div className='score'>Score: {play.score}</div>
					}

				</div>

			</div>

			<div className='spirit-list'>
				{
					play.spirits.map((playedSpirit, i) =>
						<div key={i} className='spirit'>
							<span className='spirit-name'>
								<SpiritLink spirit={playedSpirit.spirit} />
							</span>

							{
								playedSpirit.aspect &&
								<React.Fragment> - <AspectLink aspectName={playedSpirit.aspect} /></React.Fragment>
							}

							<span className="spirit-player">
								{playedSpirit.player.name}
							</span>
						</div>
					)
				}
			</div>

			{
				play.comments &&
				<React.Fragment>
					<div className='comments'>
						{
							play.comments.replace(`\n\n`, `\n`).split(`\n`).map((paragraph, i) =>
								<p key={i}>{paragraph}</p>
							)
						}
					</div>
					<div className="comments-overflow" onClick={() => setExpanded(props.alwaysExpanded || !expanded)}></div>
				</React.Fragment>
			}

		</div>
	)
}
